import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChevronRight } from 'react-icons/fa';
import { fetchAnnouncements } from '../../config';
import './AnnouncementBar.css';

/**
 * AnnouncementBar displays admin-managed announcements one by one with a slide-in
 * animation. Announcements are managed in the submission portal and served from the
 * backend (see src/config.js). If none are returned, nothing is rendered.
 */

const AnnouncementBar = () => {
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadAnnouncements = async () => {
      try {
        const data = await fetchAnnouncements();
        if (cancelled) return;
        if (Array.isArray(data) && data.length > 0 && data[0].message) {
          setAnnouncements(data);
          setVisible(true);
        } else {
          setVisible(false);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to fetch announcements', err);
        setVisible(false);
      }
    };

    loadAnnouncements();
    return () => { cancelled = true; };
  }, []);

  // Cycle through announcements every 6 seconds
  useEffect(() => {
    if (announcements.length <= 1) return; // No need to cycle
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % announcements.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [announcements]);

  const handleBarClick = (e) => {
    if (e.target.closest('.announcement-link')) return;
    navigate('/announcements');
  };

  if (!visible) return null;
  const { message, link } = announcements[currentIndex] || {};
  const content = link ? (
    <a href={link} target="_blank" rel="noopener noreferrer" className="announcement-link">
      {message}
    </a>
  ) : (
    message
  );

  return (
    <div
      className="announcement-bar slide-down"
      onClick={handleBarClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleBarClick(e)}
    >
      {/* title shows the full text on hover when the single line truncates */}
      <span key={currentIndex} className="announcement-content" title={message}>{content}</span>
      <span className="announcement-cta">
        View All <FaChevronRight className="announcement-cta-icon" />
      </span>
    </div>
  );
};

export default AnnouncementBar;
